import { supabase } from "@/integrations/supabase/client";
import type { Enums, Json, TablesInsert } from "@/integrations/supabase/types";

export function insertSupportTicket(data: TablesInsert<"support_tickets">) {
  return supabase.from("support_tickets").insert(data);
}

export type SupportAccessMode = Enums<"support_access_mode">;
export type SupportReasonType = Enums<"support_reason_type">;
export type SupportSessionStatus = Enums<"support_session_status">;

export type SupportSessionRow = {
  id: string;
  store_id: string;
  /**
   * Nullable depuis 20260830210000 : la session survit à la suppression du
   * compte de l'agent, pour que le journal d'audit ne parte pas en cascade.
   */
  support_user_id: string | null;
  access_mode: SupportAccessMode;
  reason_type: SupportReasonType;
  reason_comment: string;
  status: SupportSessionStatus;
  approved_by_user_id: string | null;
  approved_at: string | null;
  approver_first_name: string | null;
  approver_last_name: string | null;
  approver_role: string | null;
  rejected_reason: string | null;
  started_at: string | null;
  expires_at: string | null;
  ended_at: string | null;
  end_reason: string | null;
  consent_source: string | null;
  consent_conversation_ref: string | null;
  consent_obtained_from_name: string | null;
  consent_obtained_from_role: string | null;
  created_at: string;
};

export async function fetchSupportAdminRow(userId: string) {
  return supabase.from("support_admins").select("user_id, label").eq("user_id", userId).maybeSingle();
}

export async function listStoresForSupportAdmin() {
  return supabase.rpc("list_stores_for_support_admin");
}

export async function fetchMyActiveSupportSession(supportUserId: string) {
  return supabase
    .from("support_sessions")
    .select("*")
    .eq("support_user_id", supportUserId)
    .eq("status", "active")
    .maybeSingle();
}

export type ActiveSupportSessionForStore = {
  id: string;
  store_id: string;
  support_user_id: string | null;

  access_mode: SupportAccessMode;
  expires_at: string | null;
  support_label: string;
};

export async function fetchActiveSupportSessionForStoreDisplay(storeId: string) {
  const { data: row, error } = await supabase
    .from("support_sessions")
    .select("id, store_id, support_user_id, access_mode, expires_at")
    .eq("store_id", storeId)
    .eq("status", "active")
    .maybeSingle();

  if (error) return { data: null as ActiveSupportSessionForStore | null, error };
  if (!row) return { data: null as ActiveSupportSessionForStore | null, error: null };

  const { data: adm } = row.support_user_id
    ? await supabase
        .from("support_admins")
        .select("label")
        .eq("user_id", row.support_user_id)
        .maybeSingle()
    : { data: null as { label: string } | null };

  const support_label = adm?.label?.trim() || "Support Equiply";

  return {
    data: { ...row, support_label } satisfies ActiveSupportSessionForStore,
    error: null,
  };

}

export async function fetchMySupportSessions(supportUserId: string) {
  return supabase
    .from("support_sessions")
    .select("*")
    .eq("support_user_id", supportUserId)
    .order("created_at", { ascending: false })
    .limit(50);
}

export type PendingSupportSessionForStore = {
  id: string;
  store_id: string;
  support_user_id: string | null;

  access_mode: SupportAccessMode;
  reason_type: SupportReasonType;
  reason_comment: string;
  created_at: string;
  support_label: string;
};

/**
 * Demandes d'accès support en attente sur les magasins gérés, avec le nom de
 * l'agent qui les a émises — le gérant doit savoir qui il autorise avant
 * d'approuver.
 */
export async function fetchPendingSupportSessionsForStores(storeIds: string[]) {
  if (storeIds.length === 0) {
    return { data: [] as PendingSupportSessionForStore[], error: null };
  }

  const { data: rows, error } = await supabase
    .from("support_sessions")
    .select("id, store_id, support_user_id, access_mode, reason_type, reason_comment, created_at")
    .in("store_id", storeIds)
    .eq("status", "pending")
    .order("created_at", { ascending: false });

  if (error) return { data: null as PendingSupportSessionForStore[] | null, error };
  if (!rows || rows.length === 0) {
    return { data: [] as PendingSupportSessionForStore[], error: null };
  }

  const supportUserIds = [
    ...new Set(rows.map((r) => r.support_user_id).filter((id): id is string => !!id)),
  ];
  const { data: admins } = supportUserIds.length
    ? await supabase.from("support_admins").select("user_id, label").in("user_id", supportUserIds)
    : { data: [] as { user_id: string; label: string }[] };

  const labelByUserId = new Map((admins ?? []).map((a) => [a.user_id, a.label]));

  const data: PendingSupportSessionForStore[] = rows.map((row) => ({
    ...row,
    support_label:
      (row.support_user_id ? labelByUserId.get(row.support_user_id)?.trim() : "") || "Support Equiply",
  }));


  return { data, error: null };
}

export type SupportConsentRole = "franchisee" | "department_head" | "assistant_manager";

/**
 * Crée une demande d'accès au statut « en attente ». Elle n'ouvre aucun accès :
 * un gérant du magasin doit l'approuver (approveSupportSession), puis le support
 * démarre la session (startSupportSession).
 */
export function requestSupportSession(params: {
  storeId: string;
  accessMode: SupportAccessMode;
  reasonType: SupportReasonType;
  reasonComment: string;
  /** Traçabilité facultative : d'où vient la sollicitation du client. */
  consentSource?: string;
  consentConversationRef?: string;
  consentObtainedFromName?: string;
  consentObtainedFromRole?: SupportConsentRole | "";
}) {
  return supabase.rpc("request_support_session", {
    p_store_id: params.storeId,
    p_access_mode: params.accessMode,
    p_reason_type: params.reasonType,
    p_reason_comment: params.reasonComment,
    // Les types générés déclarent ces quatre paramètres obligatoires. Le SQL
    // traite la chaîne vide comme une absence (voir 20260830200000).
    p_consent_source: params.consentSource ?? "",
    p_consent_conversation_ref: params.consentConversationRef ?? "",
    p_consent_obtained_from_name: params.consentObtainedFromName ?? "",
    p_consent_obtained_from_role: params.consentObtainedFromRole ?? "",
  });
}

export async function approveSupportSession(sessionId: string) {
  return supabase.rpc("approve_support_session", { p_session_id: sessionId });
}

export async function rejectSupportSession(sessionId: string, reason?: string | null) {
  return supabase.rpc("reject_support_session", { p_session_id: sessionId, p_reason: reason ?? undefined });
}

export async function startSupportSession(sessionId: string) {
  return supabase.rpc("start_support_session", { p_session_id: sessionId });
}

export async function endSupportSession(sessionId: string, endReason?: string) {
  return supabase.rpc("end_support_session", {
    p_session_id: sessionId,
    p_end_reason: endReason ?? "manual",
  });
}

export async function cancelSupportSession(sessionId: string) {
  return supabase.rpc("cancel_support_session", { p_session_id: sessionId });
}

export function notifySupportRequest(sessionId: string) {
  return supabase.functions.invoke("notify-support-request", {
    body: { session_id: sessionId },
  });
}

export function notifySupportApproved(sessionId: string) {
  return supabase.functions.invoke("notify-support-approved", {
    body: { session_id: sessionId },
  });
}

export type SupportAuditEventListRow = {
  id: string;
  session_id: string;
  event_type: string;
  actor_user_id: string;
  actor_label: string;
  metadata: Json | null;
  created_at: string;
  support_sessions: {
    store_id: string;
    status: string;
    access_mode: string;
    reason_type: string;
    created_at: string;
  } | null;
};

export function fetchSupportAuditEvents(limit = 200) {
  return supabase
    .from("support_audit_events")
    .select(
      `
      id,
      session_id,
      event_type,
      actor_user_id,
      actor_label,
      metadata,
      created_at,
      support_sessions (
        store_id,
        status,
        access_mode,
        reason_type,
        created_at
      )
    `,
    )
    .order("created_at", { ascending: false })
    .limit(limit);
}

function csvEscapeCell(value: string): string {
  if (/[",\n\r]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

export function supportAuditEventsToCsv(rows: SupportAuditEventListRow[]): string {
  const header = [
    "created_at",
    "event_type",
    "actor_label",
    "actor_user_id",
    "session_id",
    "store_id",
    "session_status",
    "access_mode",
    "reason_type",
    "metadata_json",
  ];
  const lines = [header.join(",")];
  for (const r of rows) {
    const sess = r.support_sessions;
    const meta = r.metadata == null ? "" : JSON.stringify(r.metadata);
    lines.push(
      [
        csvEscapeCell(r.created_at),
        csvEscapeCell(r.event_type),
        csvEscapeCell(r.actor_label),
        csvEscapeCell(r.actor_user_id),
        csvEscapeCell(r.session_id),
        csvEscapeCell(sess?.store_id ?? ""),
        csvEscapeCell(sess?.status ?? ""),
        csvEscapeCell(sess?.access_mode ?? ""),
        csvEscapeCell(sess?.reason_type ?? ""),
        csvEscapeCell(meta),
      ].join(","),
    );
  }
  return "\uFEFF" + lines.join("\n");
}

// =====================================================================
// AAARR pilotage (support admins only) — server-side aggregations + pagination
// =====================================================================

export type SupportOverviewKpis = {
  signups_7d: number;
  to_activate: number;
  trial_ending_7d: number;
  referrals_converted: number;
  at_risk: number;
};

export type ActivationStoreRow = {
  id: string;
  name: string;
  created_at: string;
  age_days: number;
  subscription_status: string | null;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  employee_count: number;
  publication_count: number;
};

export type AtRiskStoreRow = {
  id: string;
  name: string;
  subscription_status: string | null;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  employee_count: number;
  last_publication_at: string | null;
  days_since_last_publication: number | null;
};

export type TrialEndingStoreRow = {
  id: string;
  name: string;
  trial_ends_at: string;
  days_left: number;
  subscription_status: string | null;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  employee_count: number;
  publication_count: number;
};

type Paged<T> = { rows: T[]; total: number };

// Cast helper: new RPCs not yet in generated types.
type RpcClient = {
  rpc: (fn: string, args?: Record<string, unknown>) => Promise<{ data: unknown; error: { message: string } | null }>;
};

export async function fetchSupportOverview(): Promise<SupportOverviewKpis> {
  const { data, error } = await (supabase as unknown as RpcClient).rpc("support_admin_overview");
  if (error) throw error;
  return (data ?? {
    signups_7d: 0,
    to_activate: 0,
    trial_ending_7d: 0,
    referrals_converted: 0,
    at_risk: 0,
  }) as SupportOverviewKpis;
}

export async function fetchActivationStores(params: {
  minDays: number;
  includePending: boolean;
  search: string;
  limit: number;
  offset: number;
}): Promise<Paged<ActivationStoreRow>> {
  const { data, error } = await (supabase as unknown as RpcClient).rpc("support_admin_list_activation", {
    p_min_days: params.minDays,
    p_include_pending: params.includePending,
    p_search: params.search,
    p_limit: params.limit,
    p_offset: params.offset,
  });
  if (error) throw error;
  const d = (data ?? { rows: [], total: 0 }) as Paged<ActivationStoreRow>;
  return { rows: d.rows ?? [], total: d.total ?? 0 };
}

export async function fetchAtRiskStores(params: {
  inactiveDays: number;
  search: string;
  limit: number;
  offset: number;
}): Promise<Paged<AtRiskStoreRow>> {
  const { data, error } = await (supabase as unknown as RpcClient).rpc("support_admin_list_at_risk", {
    p_inactive_days: params.inactiveDays,
    p_search: params.search,
    p_limit: params.limit,
    p_offset: params.offset,
  });
  if (error) throw error;
  const d = (data ?? { rows: [], total: 0 }) as Paged<AtRiskStoreRow>;
  return { rows: d.rows ?? [], total: d.total ?? 0 };
}

export async function fetchTrialEndingStores(params: {
  daysAhead: number;
  search: string;
  limit: number;
  offset: number;
}): Promise<Paged<TrialEndingStoreRow>> {
  const { data, error } = await (supabase as unknown as RpcClient).rpc("support_admin_list_trial_ending", {
    p_days_ahead: params.daysAhead,
    p_search: params.search,
    p_limit: params.limit,
    p_offset: params.offset,
  });
  if (error) throw error;
  const d = (data ?? { rows: [], total: 0 }) as Paged<TrialEndingStoreRow>;
  return { rows: d.rows ?? [], total: d.total ?? 0 };
}
